import {
  QueryObserverOptions,
  QueryOptions,
  useMutation,
  useQueries,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "react-query";
import { useFetchContext } from "../common/FetchContext";
import { urls } from "../common/urls";
import * as queryKeys from "./projectManagementQueryKeyFactory";
import { Plugin } from "./types/Plugin";
import { Group } from "./types/Group";
import { ServerUnitTypeFamily } from "./types/UnitTypeFamily";
import { ServerProcedureDefinition } from "./types/ProcedureDefinition";

export const useCustomers = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.customerKeys.all,
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.customers.all);
      return data.results;
    },
    ...options,
  });
};

export const useCustomersPi = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.customerKeys.allPi,
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.customers.allPi);
      return data.results;
    },
    ...options,
  });
};

export const useProjectManagers = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.groupKeys.projectManagers(),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.groups.projectManagers);
      return data.results[0].users;
    },
    ...options,
  });
};

export const useProjectGroup = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.groupKeys.projects(),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.groups.projects);
      return data.results[0];
    },
    ...options,
  });
};

export const useCustomerDetails = (id: string, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.customerKeys.detail(id),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.customers.details(id));
      return data;
    },
    ...options,
  });
};
export const useCustomerDetailsPi = (
  id: string,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.customerKeys.detailPi(id),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.customers.detailsPi(id));
      return data;
    },
    ...options,
  });
};

export const useProjectDetails = (
  activeProject: string | null,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.projectKeys.detail(activeProject),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(
        urls.projects.projectDetails(activeProject),
      );
      return data;
    },
    enabled: activeProject !== undefined && activeProject !== null,
    ...options,
  });
};

export const editProjectDetails = (
  id: string | null,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.projectKeys.detail(id),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.update.updateProject(id));
      return data;
    },
    enabled: id !== undefined && id !== null,
    ...options,
  });
};

export const getLastEditProjectDetails = (
  id: string | null,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.projectKeys.detail(id),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(
        urls.update.getlastUpdateProject(id),
      );
      return data;
    },
    enabled: id !== undefined && id !== null,
    ...options,
  });
};

export const useProjectSummary = (
  projectId: string,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.projectKeys.detail(projectId),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(
        urls.projects.projectIntelligenceSummary(projectId),
      );
      return data;
    },
    enabled: projectId !== undefined && projectId !== null,
    ...options,
  });
};

export const useProjectWorkOrders = (
  projectId: string,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.projectKeys.workOrders(projectId),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.projects.workOrders(projectId));

      return data;
    },
    ...options,
  });
};

export const useWorkOrdersById = (
  workOrderIds: number[],
  options: QueryObserverOptions<any, any, any> = {},
): UseQueryResult<any, any>[] => {
  const { authAxios } = useFetchContext();

  const queryOptions: UseQueryOptions<any, any, any>[] = workOrderIds.map(
    (workOrderId: number) => {
      return {
        queryKey: queryKeys.workOrderKeys.detail(workOrderId),
        queryFn: async (): Promise<any> => {
          const { data } = await authAxios.get(`work_orders/${workOrderId}/`);

          return data;
        },
        ...options,
      };
    },
  );

  return useQueries(queryOptions);
};

export const useWorkOrderDetails = (id: string, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.workOrderKeys.detail(id),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.workOrders.details(id));
      return data;
    },
    ...options,
  });
};

export const useAvailableUnits = (id: string, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.projectKeys.availableUnits(id),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.projects.availableUnits(id));
      return data;
    },
    ...options,
  });
};

export const useAssignUnitsByWorkOrder = (
  activeWorkOrder: string | null,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.workOrderKeys.assignUnits(activeWorkOrder),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(
        urls.workOrders.assignUnits(activeWorkOrder),
      );
      return data;
    },
    enabled:
      activeWorkOrder !== undefined &&
      activeWorkOrder !== null &&
      activeWorkOrder !== "",
    ...options,
  });
};

export const useAssignTSDByWorkOrde = (
  activeWorkOrder: string | null,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(
        urls.workOrders.assignTsd(activeWorkOrder),
      );
      return data.procedure_definitions;
    },
    ...options,
  });
};

export const useTestSequenceDefinitions = (
  createdWorkOrderId: any,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.testSequenceDefinitionsKeys.all,
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.testSequenceDefinitions.all);
      return data.results;
    },
    enabled:
      createdWorkOrderId !== undefined &&
      createdWorkOrderId !== null &&
      createdWorkOrderId !== "",
    ...options,
  });
};

export const useUnitTypes = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.unitTypeKeys.all,
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.unitTypes.all);
      return data.results;
    },
    ...options,
  });
};

export const useModuleTechnologies = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.moduleTechologiesKeys.all,
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.moduleTechnologies.all);
      return data.results;
    },
    ...options,
  });
};

export const useMeasurementTypes = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.measurementTypesKeys.all,
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.measurementTypes.all);
      return data.results;
    },
    ...options,
  });
};

export const useUnitTypeFamilies = (
  options: UseQueryOptions<ServerUnitTypeFamily[], any> = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<ServerUnitTypeFamily[], any>({
    queryKey: queryKeys.unitTypeFamiliesKeys.all,
    queryFn: async () => {
      const { data } = await authAxios.get(urls.unitTypeFamilies.all);

      return data.results;
    },
    ...options,
  });
};

export const useUnitTypeDetails = (
  unitTypeId: string,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.unitTypeKeys.detail(unitTypeId),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.unitTypes.details(unitTypeId));
      return data;
    },
    ...options,
  });
};

export const useReportTypes = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.reportTypesKeys.all,
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.reportTypes.all);
      return data.results;
    },
    ...options,
  });
};

export const useBurndown = (id: any) => {
  const { authAxios } = useFetchContext();

  const getBurndown = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.projects.burndownGraph(id));
    return data;
  };

  return useQuery(["burndownChart", id], getBurndown);
};

export const useGantt = (id: any) => {
  const { authAxios } = useFetchContext();

  const getGantt = async (): Promise<any> => {
    const { data } = await authAxios.get(`/projects/${id}/gantt_graph/`);
    return data;
  };

  return useQuery(["ganttChart", id], getGantt);
};

export const usePlugins = (options: QueryOptions<any, any> = {}) => {
  const { authAxios } = useFetchContext();

  const getPlugins = async (): Promise<Plugin[]> => {
    const { data } = await authAxios.get(urls.plugins.all);
    return data.results;
  };

  return useQuery<Plugin[], any>(queryKeys.pluginKeys.all, getPlugins, options);
};

export const usePlugin = (
  pluginId: number,
  options: QueryOptions<any, any> = {},
) => {
  const { authAxios } = useFetchContext();

  const getPlugin = async (): Promise<Plugin> => {
    const { data } = await authAxios.get(urls.plugins.details(pluginId));

    return data;
  };

  return useQuery<Plugin, any>(
    queryKeys.pluginKeys.detail(pluginId),
    getPlugin,
    options,
  );
};

export const useGroups = (options: UseQueryOptions<Group[], any> = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<Group[], any>({
    queryKey: queryKeys.groupKeys.all,
    queryFn: async (): Promise<Group[]> => {
      const { data } = await authAxios.get(urls.groups.all);

      return data.results;
    },
    ...options,
  });
};

export const useGroupsByGroupType = (
  groupType: number,
  options: UseQueryOptions<Group[], any> = {},
) => {
  const { authAxios } = useFetchContext();

  const getGroupsByGroupType = async (): Promise<Group[]> => {
    const { data } = await authAxios.get(`groups/?group_type=${groupType}`);

    return data.results;
  };

  return useQuery<Group[], any>(
    queryKeys.groupKeys.groupType(groupType),
    getGroupsByGroupType,
    options,
  );
};

export const useProcedureDefinitions = (
  useOnlyAvailable: boolean = false,
  options: UseQueryOptions<ServerProcedureDefinition[], any> = {},
) => {
  const { authAxios } = useFetchContext();

  const getProcedureDefinitions = async () => {
    let requestString = "procedure_definitions/?limit=-1";

    if (useOnlyAvailable) requestString += "&disposition=16";

    const { data } = await authAxios.get(requestString);

    return data.results;
  };

  return useQuery<ServerProcedureDefinition[], any>(
    queryKeys.procedureDefinitionKeys.all,
    getProcedureDefinitions,
    options,
  );
};

export const useProjectActions = (id: string, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.projectKeys.actions(id),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.projects.actions(id));

      return data;
    },
    ...options,
  });
};

export const useActionDefinitions = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.actionDefinitionKeys.all,
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.actionDefinitions.all);

      return data.results;
    },
    ...options,
  });
};

export const useActionResult = (id: string, options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.actionDefinitionKeys.detail(id),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.actionResults.details(id));

      return data;
    },
    ...options,
  });
};

export const useCheckActionCompletion = (
  id: string,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.actionDefinitionKeys.detail(id),
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.actionResults.completed(id));

      return data;
    },
    enabled: false,
    ...options,
  });
};

export const useReports = (options: UseQueryOptions<any, any> = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.actionResults.reports);

      return data;
    },
    ...options,
  });
};

export const useFaildReports = () => {
  const getFailedReports = async (params: any): Promise<any> => {
    const { authAxios, startDate, endDate, filterType, tsd_ids } = params;
    const { data } = await authAxios.get(
      urls.actionResults.faild_report(startDate, endDate, filterType, tsd_ids),
      {},
    );
    return data;
  };
  return useMutation(getFailedReports);
};

export const useRetestRequiredReports = () => {
  const getRetestRequiredReports = async (params: any): Promise<any> => {
    const { authAxios, startDate, endDate } = params;
    const { data } = await authAxios.get(
      urls.actionResults.retest_Required_Report(startDate, endDate),
      {},
    );
    return data;
  };
  return useMutation(getRetestRequiredReports);
};

export const useFlashReports = () => {
  const getFlashReports = async (params: any): Promise<any> => {
    const { authAxios, startDate, endDate } = params;
    const { data } = await authAxios.get(
      urls.actionResults.flash_report(startDate, endDate),
      {},
    );
    return data;
  };
  return useMutation(getFlashReports);
};

export const updateCorrection = async (params: any): Promise<any> => {
  const { authAxios, id, ...postParams } = params;
  const { data } = await authAxios.put(urls.actionResults.update, {
    ...postParams,
  });
  return data;
};

export const useUpdateCorrection = () => {
  return useMutation(updateCorrection);
};

// export const useUvidFeFlashReports = () => {
//   const getFlashReports = async (params: any): Promise<any> => {
//     const { authAxios, month, startDate, endDate, limit, offset} = params;
//     const { data } = await authAxios.get(urls.actionResults.uvid_fe_flash(month,startDate,endDate,limit, offset), {
//     });
//   return data;
// };
//   return useMutation(getFlashReports);
// };

export const useUvidFeFlashReports = (
  month: any,
  startDate: any,
  endDate: any,
  offset: any,
  limit: any,
  customer: any,
  projectNumber: any,
  serialNumber: any,
  workOrder: any,
  tsdName: any,
  action: any,
) => {
  const { authAxios } = useFetchContext();

  const getFlashReports = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.actionResults.uvid_fe_flash(
        month,
        startDate,
        endDate,
        customer,
        projectNumber,
        serialNumber,
        workOrder,
        tsdName,
        offset,
        limit,
      ),
    );
    return data;
  };
  return useQuery(["flashData", action], getFlashReports);
};

//For delivarables
export const useDeliverables = (id: string) => {
  const { authAxios } = useFetchContext();
  const deliverablesData = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.deliverables.deliverablescoverdata(id),
    );
    return data;
  };
  return useQuery(["deliverablesData", id], deliverablesData);
};

const uvidFeFileUpload = async (
  authAxios: any,
  azure_file_id: string,
): Promise<any> => {
  const { data } = await authAxios.post(
    urls.actionResults.uvid_fe_flash_fileUpload,
    {
      azure_file_id,
    },
  );
  return data;
};

export const useAvailableCheckList = (
  report_result_id: string,
  options?: {
    enabled?: boolean;
  },
) => {
  const { authAxios } = useFetchContext();

  const getAvailableCheckList = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.reportWriterAgenda.available_Check_List(report_result_id),
    );

    return data.results;
  };

  return useQuery(
    ["availableCheckList", report_result_id],
    getAvailableCheckList,
    {
      enabled: options?.enabled ?? true,
    },
  );
};

export const useCheckList = (p0: { enabled: boolean }) => {
  const { authAxios } = useFetchContext();

  const getWriterData = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.reportWriterAgenda.Check_List_Data,
    );
    return data.results;
  };

  return useQuery(["Check_List_data"], getWriterData, {});
};

export const useReportApprover = (
  page: any,
  itemsPerPage: any,
  action: number,
) => {
  const { authAxios } = useFetchContext();

  const getApproverData = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.reportApproversAgenda.submit(page, itemsPerPage),
    );
    return data;
  };

  return useQuery(
    ["Approver_data", page * 10, itemsPerPage, action],
    getApproverData,
    {
      keepPreviousData: false,
    },
  );
};

export const usePendingCheckList = (
  report_result_id: string,
  options?: {
    enabled?: boolean;
  },
) => {
  const { authAxios } = useFetchContext();

  const getPendingCheckList = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.reportWriterAgenda.pending_Check_List(report_result_id),
    );

    return data.results;
  };

  return useQuery(["pendingCheckList", report_result_id], getPendingCheckList, {
    enabled: options?.enabled ?? true,
  });
};

export const useReportWriter = (
  page: any,
  itemsPerPage: any,
  action: number,
) => {
  const { authAxios } = useFetchContext();

  const getWriterData = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.reportWriterAgenda.submit(page, itemsPerPage),
    );
    return data;
  };

  // Dynamic query key including page and itemsPerPage
  return useQuery(
    ["Writer_data", page * 10, itemsPerPage, action],
    getWriterData,
    {
      keepPreviousData: false, // This keeps the previous data while fetching new data
    },
  );
};

export const usePretWriterQueue = (
  page: any,
  itemsPerPage: any,
  action: number,
) => {
  const { authAxios } = useFetchContext();

  const getPreWriterData = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.preWriterQueue.submit(page, itemsPerPage),
    );
    return data;
  };

  // Dynamic query key including page and itemsPerPage
  return useQuery(
    ["Pre_Writer_data", page * 10, itemsPerPage, action],
    getPreWriterData,
    {
      keepPreviousData: false, // This keeps the previous data while fetching new data
    },
  );
};

export const useReportFileTemplate = (id: string) => {
  const { authAxios } = useFetchContext();

  const getFilename = async () => {
    const { data } = await authAxios.get(
      urls.reportFileTemplate.viewTemplate(id),
    );
    return data;
  };

  return useQuery(["reportfile", id], getFilename, {
    enabled: !!id,
  });
};

export const useUvidFeFileUpload = () => {
  const { authAxios } = useFetchContext();

  return useMutation((azure_file_id: string) =>
    uvidFeFileUpload(authAxios, azure_file_id),
  );
};

export const deliverablesFlashData = (
  work_order_id: string,
  procedure_definition_id: string,
) => {
  const { authAxios } = useFetchContext();
  const deliverablesData = async (): Promise<any> => {
    const { data } = await authAxios.post(
      urls.deliverables.deliverableFlashData,
      {
        work_order_id: work_order_id,
        procedure_definition_id: procedure_definition_id,
      },
    );
    return data;
  };
  return useQuery(
    ["deliverableFlashData", work_order_id, procedure_definition_id],
    deliverablesData,
  );
};

export const deliverablesElData = (
  work_order_id: string,
  procedure_definition_id: string,
) => {
  const { authAxios } = useFetchContext();
  const deliverablesData = async (): Promise<any> => {
    const { data } = await authAxios.post(urls.deliverables.deliverableELData, {
      work_order_id: work_order_id,
      procedure_definition_id: [procedure_definition_id],
    });
    return data;
  };
  return useQuery(
    ["deliverableELData", work_order_id, procedure_definition_id],
    deliverablesData,
  );
};

// for rsd

export const useReportDefinitions = (
  useOnlyAvailable: boolean = false,
  options: UseQueryOptions<ServerProcedureDefinition[], any> = {},
) => {
  const { authAxios } = useFetchContext();

  const getProcedureDefinitions = async () => {
    let requestString = "reporttypedefinition/";

    const { data } = await authAxios.get(requestString);

    return data.results;
  };

  return useQuery<ServerProcedureDefinition[], any>(
    queryKeys.reportDefinitionsKeys.all,
    getProcedureDefinitions,
    options,
  );
};

export const useProductTypes = (
  useOnlyAvailable: boolean = false,
  options: UseQueryOptions<ServerProcedureDefinition[], any> = {},
) => {
  const { authAxios } = useFetchContext();

  const getProcedureDefinitions = async () => {
    let requestString = "producttypedefinition/";

    const { data } = await authAxios.get(requestString);

    return data.results;
  };

  return useQuery<ServerProcedureDefinition[], any>(
    queryKeys.productTypeKeys.all,
    getProcedureDefinitions,
    options,
  );
};

export const useExpectedUnitTypeList = (
  id: string,
  options: QueryOptions = {},
) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(
        urls.expectedUnitTypes.expectedUnitTypes(id),
      );
      return data;
    },
    ...options,
  });
};

//reportSequenceDefinition

export const useReportSequenceTypes = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.reportSequenceKeys.all,
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.reportSequenceDefinitions.all);
      return data.results;
    },
    ...options,
  });
};

//Pre- writer queue status view
export const useStatusView = (report_result_id: string) => {
  const { authAxios } = useFetchContext();
  const getStatusView = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.preWriterQueue.status_view(report_result_id),
    );

    return data.incomplete_procedures;
  };
  return useQuery(["statusView", report_result_id], getStatusView, {});
};

//createReportResult
export const useCreateReportResult = (options: any = {}) => {
  const { authAxios } = useFetchContext();

  return useMutation<any, any, PostReportSequenceProps>({
    mutationFn: async ({
      work_order_id,
      report_sequence_definition_id,
      username,
    }: PostReportSequenceProps): Promise<any> => {
      const { data } = await authAxios.post(urls.createReportResult.submit, {
        work_order_id,
        report_sequence_definition_id,
        username,
      });
      return data;
    },
    ...options,
  });
};

//reportResult
export const useReportResult = (work_order_id: string) => {
  const { authAxios } = useFetchContext();
  const reportResultDataData = async (): Promise<any> => {
    const { data } = await authAxios.get(urls.reportResult.all(work_order_id));
    return data.results;
  };
  return useQuery(["reportResultData", work_order_id], reportResultDataData);
};

//reportDispostion
export const useReportDispostion = (options: QueryOptions = {}) => {
  const { authAxios } = useFetchContext();

  return useQuery<any, any>({
    queryKey: queryKeys.reportDispostionKeys.all,
    queryFn: async (): Promise<any> => {
      const { data } = await authAxios.get(urls.reportDispositions.all);
      return data;
    },
    ...options,
  });
};

export interface PostReportSequenceProps {
  work_order_id: string | number;
  report_sequence_definition_id: string | number;
  username: string | null;
}

export const useRetestEdgePinchGet = (
  serial_number: string,
  enabled = true,
) => {
  const { authAxios } = useFetchContext();

  const getRetestEdgepinchget = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.EdgePinchUpload.retestgetEdgepinch(serial_number),
    );

    return data;
  };

  return useQuery(
    ["retestEdgePinchData", serial_number],
    getRetestEdgepinchget,
    {
      enabled: !!serial_number && enabled,
    },
  );
};

export const useWorkOrderList = (projectId: string) => {
  const { authAxios } = useFetchContext();

  const getWorkorderList = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.EdgePinchUpload.Workorderlist(projectId),
    );

    return data;
  };

  return useQuery(["WorkOrderList", projectId], getWorkorderList, {
    enabled: !!projectId,
  });
};

export const useEdgePinchProjectslist = () => {
  const { authAxios } = useFetchContext();
  const getEdgepinchProjectDetails = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.EdgePinchUpload.EdgePinchProjectGet,
    );

    return data;
  };
  return useQuery(["getEdgePinchProjectDetails"], getEdgepinchProjectDetails);
};

export const DocumentUploadReport = (template_id: string) => {
  const { authAxios } = useFetchContext();

  const DocumnetPreviewData = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.reportWriterAgenda.DocumentUpload_Preview(template_id),
    );

    return data;
  };

  return useQuery([template_id], DocumnetPreviewData, {
    enabled: !!template_id,
  });
};

export const useCreateReport = () => {
  const { authAxios } = useFetchContext();

  return useMutation(
    async ({
      report_result_id,
      template,
    }: {
      report_result_id: number;
      template: string;
    }) => {
      const { data } = await authAxios.get(
        urls.docgen.reportTemplate(report_result_id, template),
      );

      return data;
    },
  );
};
export const useContinueReportWriting = () => {
  const { authAxios } = useFetchContext();

  return useMutation(
    async ({ reportId, template }: { reportId: number; template: string }) => {
      const { data } = await authAxios.get(
        urls.docgen.GetreportTemplateData(reportId, template),
      );

      return data;
    },
  );
};

export const useReportIntakeImages = (workorderId: string) => {
  const { authAxios } = useFetchContext();

  const getInatkeImageList = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.docgen.GetreportIntakeImages(workorderId),
    );

    return data;
  };

  return useQuery(["intakeImagesList", workorderId], getInatkeImageList, {
    enabled: !!workorderId,
  });
};

export const DocumentTempDelete = async (params: any): Promise<any> => {
  const { authAxios, template_id } = params; // Extract template_id

  const { data } = await authAxios.delete(
    urls.reportWriterAgenda.UploadTemplateDelete(template_id),
  );
  return data;
};

export const useDocumentTempDelete = () => {
  return useMutation(DocumentTempDelete);
};
export const useDocumentTempData = () => {
  const { authAxios } = useFetchContext();
  const getDocumentUploadTempData = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.reportWriterAgenda.DocumentUploadTempData,
    );

    return data;
  };
  return useQuery(["getDocumentTempData"], getDocumentUploadTempData);
};

export const useGetVerifierComments = (
  report_result_id: string,
  is_writer: boolean,
  options?: { enabled?: boolean },
) => {
  const { authAxios } = useFetchContext();
  const GetAllCommentsData = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.reportWriterAgenda.WriterGetAllviewComments(
        report_result_id,
        is_writer,
      ),
    );

    return data;
  };

  return useQuery(
    ["GetverifierComments", report_result_id, is_writer],
    GetAllCommentsData,
    {
      enabled: options?.enabled ?? true,
    },
  );
};

export const moveReport = async (params: any): Promise<any> => {
  const { authAxios, ...postParams } = params;
  const { data } = await authAxios.post(urls.docgen.moveReport, {
    ...postParams,
  });
  return data;
};

export const useMoveReport = () => {
  return useMutation(moveReport);
};

export const useReportTemplateId = () => {
  const { authAxios } = useFetchContext();

  return useMutation(async (reportTypeId: string) => {
    const { data } = await authAxios.get(
      urls.reportWriterAgenda.getTemplateID(reportTypeId),
    );
    return data;
  });
};

//Document Template Delete

export const DeletedComments = async (params: any): Promise<any> => {
  const { authAxios, note_id } = params; // Extract template_id

  const { data } = await authAxios.get(
    urls.reportWriterAgenda.DocumentCommentsDelete(note_id),
  );
  return data;
};

export const useDeleteDocComments = () => {
  return useMutation(DeletedComments);
};

export const useProjectReportNotes = (
  projectId: number,
  options?: { enabled?: boolean },
) => {
  const { authAxios } = useFetchContext();
  const GetProjectReportNotes = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.docgen.GetreportProjectNotes(projectId),
    );

    return data;
  };

  return useQuery(
    ["GetProjectReportNotes", projectId],
    GetProjectReportNotes,

  );
};

export const useGetAllModules = (tsdId: string, workOrderId: string) => {
  const { authAxios } = useFetchContext();

  const GetAllModules = async (): Promise<any> => {
    const { data } = await authAxios.get(
      urls.workOrders.getAllModules(tsdId, workOrderId),
    );

    return data;
  };

  return useQuery(["GetAllModules", tsdId, workOrderId], GetAllModules, {
    enabled: Boolean(tsdId && workOrderId), // Prevents request when parameters are missing
  });
};